import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, useTheme, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Warning, Error as ErrorIcon, Info, Delete, Flag } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTenders } from '../../hooks/TenderContext';
import { formatDate, getAlertColor, getAlertBgColor } from '../../utils/helpers';

const AlertsSection = () => {
  const theme = useTheme();
  const { alerts, setSelectedCategory } = useTenders();

  // State for flagged and dismissed alerts
  const [flaggedAlerts, setFlaggedAlerts] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('flaggedAlerts') || '[]'); } catch { return []; }
  });
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissedAlerts') || '[]'); } catch { return []; }
  });
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Filter: remove alerts that are more than 1 day past, and dismissed ones
  const visibleAlerts = alerts.filter(a => {
    if (dismissedAlerts.includes(a.id)) return false;
    if (a.daysLeft < -1) return false; // auto-remove after 1 day past
    return true;
  });

  if (visibleAlerts.length === 0) return null;

  const getIcon = (level) => {
    switch (level) {
      case 'critical': return <ErrorIcon sx={{ fontSize: 20 }} />;
      case 'warning': return <Warning sx={{ fontSize: 20 }} />;
      default: return <Info sx={{ fontSize: 20 }} />;
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'critical': return 'דחוף';
      case 'warning': return 'אזהרה';
      case 'passed': return 'עבר';
      default: return '';
    }
  };

  const handleContextMenu = (e, alert) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedAlert(alert);
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
  };

  const handleDismiss = () => {
    if (selectedAlert) {
      const updated = [...dismissedAlerts, selectedAlert.id];
      setDismissedAlerts(updated);
      sessionStorage.setItem('dismissedAlerts', JSON.stringify(updated));
    }
    setContextMenu(null);
  };

  const handleFlag = () => {
    if (selectedAlert) {
      const isFlagged = flaggedAlerts.includes(selectedAlert.id);
      const updated = isFlagged
        ? flaggedAlerts.filter(id => id !== selectedAlert.id)
        : [...flaggedAlerts, selectedAlert.id];
      setFlaggedAlerts(updated);
      sessionStorage.setItem('flaggedAlerts', JSON.stringify(updated));
    }
    setContextMenu(null);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        התראות ({visibleAlerts.length})
      </Typography>
      <Box sx={{
        maxHeight: 420,
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 1.5,
        pr: 0.5,
      }}>
        <AnimatePresence>
          {visibleAlerts.map((alert, index) => {
            const isFlagged = flaggedAlerts.includes(alert.id);
            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card
                  onContextMenu={(e) => handleContextMenu(e, alert)}
                  onClick={() => {}}
                  sx={{
                    cursor: 'context-menu',
                    bgcolor: isFlagged ? '#FFEBEE' : getAlertBgColor(alert.level),
                    border: `1px solid ${isFlagged ? '#C62828' : getAlertColor(alert.level)}30`,
                    borderRight: `4px solid ${isFlagged ? '#C62828' : getAlertColor(alert.level)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${getAlertColor(alert.level)}20`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: isFlagged ? '#C62828' : getAlertColor(alert.level) }}>
                        {isFlagged ? <Flag sx={{ fontSize: 20 }} /> : getIcon(alert.level)}
                      </Box>
                      <Chip
                        label={getLevelLabel(alert.level)}
                        size="small"
                        sx={{
                          bgcolor: isFlagged ? '#C62828' : getAlertColor(alert.level),
                          color: '#fff',
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={alert.type}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderColor: getAlertColor(alert.level),
                          color: getAlertColor(alert.level),
                        }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {alert.tenderSubject}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(alert.date)}
                      </Typography>
                      <Typography variant="caption" fontWeight={600} sx={{ color: getAlertColor(alert.level) }}>
                        {alert.daysLeft < 0
                          ? `עבר לפני ${Math.abs(alert.daysLeft)} ימים`
                          : alert.daysLeft === 0
                          ? 'היום!'
                          : `בעוד ${alert.daysLeft} ימים`}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>

      {/* Right-click context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleFlag}>
          <ListItemIcon><Flag fontSize="small" color={selectedAlert && flaggedAlerts.includes(selectedAlert.id) ? 'default' : 'error'} /></ListItemIcon>
          <ListItemText>
            {selectedAlert && flaggedAlerts.includes(selectedAlert.id) ? 'הסר דגל' : 'סמן בדגל'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDismiss}>
          <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
          <ListItemText>מחק התראה</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AlertsSection;
